import styled from "@emotion/styled";
import classNames from "classnames";
import { useMemo, useState, type ComponentProps, type MouseEvent } from "react";

import { IconButton, PanelDragItem, SkiliticsIcon } from "@skilitics/designer-ui-components";

import type { DesignerEffect } from "../../types";
import { EFFECT_SORTABLE_ZONE, EFFECT_TYPE, TYPE_INSTANCE } from "../constants";
import type { DraggableData, DroppableData } from "../dnd/dndSchemas";
import { SortableItem } from "../dnd/SortableItem";
import { EditPopover } from "./EditPopover";
import { EffectDesigner } from "./EffectDesigner";
import { ConditionOrEffectIconContainer, EditButtonContainer } from "./shared";

const EffectDragItem = styled(PanelDragItem)`
  display: grid;
  grid-template-columns: 30px 1fr 28px;
  gap: 10px;
  background: ${({ theme }) => theme.surfaceVariant};
`;

type Props = {
  effect: DesignerEffect;
  index: number;
  onRemoveEffect: (effectId: number) => void;
};

export const EffectsListItem = ({ effect, index, onRemoveEffect }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const handlePopoverClick = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleRemoveEffect = () => {
    onRemoveEffect(effect.id);
    handlePopoverClose();
  };

  const icon: ComponentProps<typeof SkiliticsIcon>["icon"] = useMemo(() => {
    switch (effect.builder.category) {
      case "navigate":
        return "effect-navigate";
      case "interludes":
        return "view-layers";
      default:
        return "effect-action";
    }
  }, [effect.builder.category]);

  return (
    <SortableItem
      key={`${effect.id}`}
      id={`${effect.id}`}
      index={index}
      group={EFFECT_SORTABLE_ZONE}
      data={
        {
          title: effect.spec.title,
          rule: {
            type: TYPE_INSTANCE,
            ruleType: EFFECT_TYPE,
            id: effect.id,
            typeId: effect.builder.typeId,
          },
        } satisfies DraggableData & DroppableData
      }
      accepts={{ "rule.ruleType": [EFFECT_TYPE] }}
      color="warning"
    >
      <EffectDragItem
        key={effect.id}
        onClick={handlePopoverClick}
        className={classNames({
          orange: true,
          active: Boolean(anchorEl),
          disabled: !effect.isValid,
        })}
      >
        <ConditionOrEffectIconContainer>
          <SkiliticsIcon icon={!effect.isValid ? "hidden-off" : icon} />
        </ConditionOrEffectIconContainer>
        <EffectDesigner effect={effect} view="summary" />
        <EditButtonContainer onClick={handlePopoverClick}>
          <IconButton icon="pencil-alt" data-test="edit-button" />
        </EditButtonContainer>
      </EffectDragItem>

      <EditPopover
        id={`edit-effect-popover-${effect.id}`}
        title={effect.spec.title}
        description={effect.spec.description}
        conditionOrEffect="effect"
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        onRemove={handleRemoveEffect}
      >
        <EffectDesigner effect={effect} view="edit" />
      </EditPopover>
    </SortableItem>
  );
};
